/**
 * Mission Studio API fetch wrappers.
 *
 * Pure functions — pair with React Query hooks in queries.ts. Use the
 * shared `apiFetch` for cookie/CSRF and base-URL handling. All endpoints
 * are staff-only (IsAdminUser on the backend); player-facing surfaces
 * land in a future phase.
 */

import type { EntitySearchResult } from '@/components/EntitySearchField';
import { apiFetch } from '@/evennia_replacements/api';
import { fetchAllPages } from '@/lib/pagination';

import type {
  BeatView,
  GroupBeatResult,
  JournalEntry,
  PendingMissionInvite,
  MissionCategory,
  MissionGiver,
  MissionGiverRequest,
  MissionInstance,
  MissionInviteResult,
  MissionNode,
  MissionOption,
  MissionOptionRoute,
  MissionOptionRouteCandidate,
  MissionOptionRouteReward,
  MissionReportResult,
  MissionTemplate,
  MissionTemplateDetail,
  GraphProblem,
  MissionTemplateFilters,
  OptionKind,
  OutcomeTier,
  PaginatedResponse,
  ReportStyle,
  ResolvedBeat,
} from './types';

const BASE_URL = '/api/missions';

export class ApiValidationError extends Error {
  readonly fieldErrors: Record<string, unknown>;
  constructor(detail: unknown) {
    super('Validation error');
    this.name = 'ApiValidationError';
    this.fieldErrors =
      typeof detail === 'object' && detail !== null ? (detail as Record<string, unknown>) : {};
  }
}

/**
 * Recursively flatten a DRF error shape into a single readable string.
 *
 * DRF can return deeply nested error bodies (e.g. nested serializer errors,
 * list-of-object errors). This collapses them into a human-readable sentence.
 * Used by CreateMissionPage and StaffActionsCard to surface API errors inline.
 */
export function flattenErrorMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const flat = value.map(flattenErrorMessage).filter(Boolean);
    return flat.length > 0 ? flat[0] : '';
  }
  if (value !== null && typeof value === 'object') {
    const parts: string[] = [];
    for (const [k, v] of Object.entries(value)) {
      const sub = flattenErrorMessage(v);
      if (sub) parts.push(`${k}: ${sub}`);
    }
    return parts.join('; ');
  }
  return String(value);
}

/** Throw an Error whose message is the server's `detail` (409s) or flattened field errors. */
async function throwServerError(res: Response, fallback: string): Promise<never> {
  const body: unknown = await res.json().catch(() => null);
  const detail =
    body !== null && typeof body === 'object' && 'detail' in body
      ? (body as { detail: unknown }).detail
      : body;
  const message = detail === null || detail === undefined ? '' : flattenErrorMessage(detail);
  throw new Error(message || fallback);
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function buildQueryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// MissionTemplate (D1)
// ---------------------------------------------------------------------------

export async function listMissionTemplates(
  filters: MissionTemplateFilters & { page?: number; page_size?: number } = {}
): Promise<PaginatedResponse<MissionTemplate>> {
  const res = await apiFetch(`${BASE_URL}/templates/${buildQueryString(filters)}`);
  if (!res.ok) throw new Error('Failed to load mission templates');
  return res.json();
}

export async function getMissionTemplate(id: number): Promise<MissionTemplateDetail> {
  const res = await apiFetch(`${BASE_URL}/templates/${id}/`);
  if (!res.ok) throw new Error(`Failed to load template ${id}`);
  return res.json();
}

export async function patchMissionTemplate(
  id: number,
  body: Partial<MissionTemplate>
): Promise<MissionTemplate> {
  const res = await apiFetch(`${BASE_URL}/templates/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiValidationError(detail);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// MissionTemplate create
// ---------------------------------------------------------------------------

export async function createMissionTemplate(
  body: Partial<MissionTemplate>
): Promise<MissionTemplate> {
  const res = await apiFetch(`${BASE_URL}/templates/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiValidationError(detail);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// MissionCategory read-only browse
// ---------------------------------------------------------------------------

/**
 * Loads up to 100 categories in one request (backend's max_page_size cap).
 * If the category set ever exceeds 100, switch to useInfiniteQuery or
 * surface a paginated picker UI.
 */
export async function listMissionCategories(): Promise<PaginatedResponse<MissionCategory>> {
  const res = await apiFetch(`${BASE_URL}/categories/?page_size=100`);
  if (!res.ok) throw new Error('Failed to load categories');
  return res.json();
}

// ---------------------------------------------------------------------------
// MissionNode (D2)
// ---------------------------------------------------------------------------

/**
 * Every row of one mission-graph list, across pages (#4558). The graph page,
 * the delete confirmation and the problem marks all reason about the WHOLE
 * graph; page 1 alone (25 rows) silently dropped arrows from any mission with
 * more routes than that. Returned in the paginated shape callers already read.
 */
async function listWholeGraph<T>(
  path: string,
  filters: object,
  errorMessage: string
): Promise<PaginatedResponse<T>> {
  const query = buildQueryString({ ...filters, page_size: GRAPH_PAGE_SIZE });
  const results = await fetchAllPages<T>(`${BASE_URL}/${path}/${query}`, errorMessage);
  return { count: results.length, next: null, previous: null, results };
}

/** The Studio API's max_page_size, so a whole graph takes as few requests as possible. */
const GRAPH_PAGE_SIZE = 100;

export async function listMissionNodes(
  filters: {
    template?: number;
    is_entry?: boolean;
    needs_rewrite?: boolean;
    page?: number;
    page_size?: number;
  } = {}
): Promise<PaginatedResponse<MissionNode>> {
  if (filters.page === undefined) {
    return listWholeGraph<MissionNode>('nodes', filters, 'Failed to load nodes');
  }
  const res = await apiFetch(`${BASE_URL}/nodes/${buildQueryString(filters)}`);
  if (!res.ok) throw new Error('Failed to load nodes');
  return res.json();
}

export async function getMissionNode(id: number): Promise<MissionNode> {
  const res = await apiFetch(`${BASE_URL}/nodes/${id}/`);
  if (!res.ok) throw new Error(`Failed to load node ${id}`);
  return res.json();
}

export async function patchMissionNode(
  id: number,
  body: Partial<MissionNode>
): Promise<MissionNode> {
  const res = await apiFetch(`${BASE_URL}/nodes/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update node');
  return res.json();
}

export async function getMissionOption(id: number): Promise<MissionOption> {
  const res = await apiFetch(`${BASE_URL}/options/${id}/`);
  if (!res.ok) throw new Error(`Failed to load option ${id}`);
  return res.json();
}

export async function patchMissionOption(
  id: number,
  body: Partial<MissionOption>
): Promise<MissionOption> {
  const res = await apiFetch(`${BASE_URL}/options/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update option');
  return res.json();
}

// ---------------------------------------------------------------------------
// MissionOption / Route / Candidate / Reward (D2)
// ---------------------------------------------------------------------------

export async function listMissionOptions(
  filters: {
    node?: number;
    template?: number;
    needs_rewrite?: boolean;
  } = {}
): Promise<PaginatedResponse<MissionOption>> {
  return listWholeGraph<MissionOption>('options', filters, 'Failed to load options');
}

export async function listMissionRoutes(
  filters: {
    option?: number;
    template?: number;
    needs_rewrite?: boolean;
  } = {}
): Promise<PaginatedResponse<MissionOptionRoute>> {
  return listWholeGraph<MissionOptionRoute>('routes', filters, 'Failed to load routes');
}

export async function listRouteCandidates(
  filters: {
    route?: number;
    template?: number;
  } = {}
): Promise<PaginatedResponse<MissionOptionRouteCandidate>> {
  return listWholeGraph<MissionOptionRouteCandidate>(
    'route-candidates',
    filters,
    'Failed to load candidates'
  );
}

export async function listRouteRewards(
  filters: {
    route?: number;
    candidate?: number;
  } = {}
): Promise<PaginatedResponse<MissionOptionRouteReward>> {
  const res = await apiFetch(`${BASE_URL}/route-rewards/${buildQueryString(filters)}`);
  if (!res.ok) throw new Error('Failed to load rewards');
  return res.json();
}

// NPC-mediated mission-giver editor moved to the npc-services framework
// per #686 (Phase 3). NPCRole + NPCServiceOffer + MissionOfferDetails now
// own that surface; a dedicated /api/npc-services/ editor is the follow-up.

// ---------------------------------------------------------------------------
// Graph authoring (#4558): create / delete nodes, options, routes
// ---------------------------------------------------------------------------

export async function createMissionNode(body: {
  template: number;
  key: string;
  is_entry?: boolean;
  conflict_mode: MissionNode['conflict_mode'];
}): Promise<MissionNode> {
  const res = await apiFetch(`${BASE_URL}/nodes/`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to add node');
  return res.json();
}

export async function deleteMissionNode(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/nodes/${id}/`, { method: 'DELETE' });
  if (!res.ok) await throwServerError(res, 'Failed to delete node');
}

export async function createMissionOption(body: {
  node: number;
  key: string;
  option_kind: OptionKind;
  order: number;
  source_kind?: 'authored';
}): Promise<MissionOption> {
  const res = await apiFetch(`${BASE_URL}/options/`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to add option');
  return res.json();
}

export async function deleteMissionOption(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/options/${id}/`, { method: 'DELETE' });
  if (!res.ok) await throwServerError(res, 'Failed to delete option');
}

export async function createMissionRoute(
  body: Partial<MissionOptionRoute> & { option: number }
): Promise<MissionOptionRoute> {
  const res = await apiFetch(`${BASE_URL}/routes/`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to add route');
  return res.json();
}

export async function patchMissionRoute(
  id: number,
  body: Partial<MissionOptionRoute>
): Promise<MissionOptionRoute> {
  const res = await apiFetch(`${BASE_URL}/routes/${id}/`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to update route');
  return res.json();
}

export async function deleteMissionRoute(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/routes/${id}/`, { method: 'DELETE' });
  if (!res.ok) await throwServerError(res, 'Failed to delete route');
}

export async function getGraphCheck(templateId: number): Promise<GraphProblem[]> {
  const res = await apiFetch(`${BASE_URL}/templates/${templateId}/graph-check/`);
  if (!res.ok) await throwServerError(res, 'Failed to check the mission graph');
  const data = (await res.json()) as { problems: GraphProblem[] };
  return data.problems;
}

export async function listOutcomeTiers(): Promise<OutcomeTier[]> {
  return fetchAllPages<OutcomeTier>(
    `${BASE_URL}/outcome-tiers/?page_size=200`,
    'Failed to load outcome tiers'
  );
}

// ---------------------------------------------------------------------------
// Copy actions (D4.2)
// ---------------------------------------------------------------------------

export async function copyTemplate(
  id: number,
  body: { new_name?: string }
): Promise<MissionTemplate> {
  const res = await apiFetch(`${BASE_URL}/templates/${id}/copy/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Mirror createMissionTemplate: parse the body and throw ApiValidationError
    // so consumers (StaffActionsCard CopyRow) can surface specific field messages
    // (e.g. {"new_name": ["May not be blank."]}), not just "Failed to copy template".
    const detail = await res.json().catch(() => ({}));
    throw new ApiValidationError(detail);
  }
  return res.json();
}

export async function copyNode(id: number, body: { new_key: string }): Promise<MissionNode> {
  const res = await apiFetch(`${BASE_URL}/nodes/${id}/copy/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to copy node');
  return res.json();
}

export async function copySubtree(
  id: number,
  body: { new_key_prefix: string }
): Promise<MissionNode> {
  const res = await apiFetch(`${BASE_URL}/nodes/${id}/copy-subtree/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwServerError(res, 'Failed to copy subtree');
  return res.json();
}

// ---------------------------------------------------------------------------
// Staff-power (D4.3)
// ---------------------------------------------------------------------------

export async function assignMission(
  id: number,
  body: { character: number }
): Promise<MissionInstance> {
  const res = await apiFetch(`${BASE_URL}/templates/${id}/assign/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to assign mission');
  return res.json();
}

export async function deleteMissionInstance(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/instances/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to remove instance');
}

// ---------------------------------------------------------------------------
// Predicate-leaf catalog (D5)
// ---------------------------------------------------------------------------

/** Type tags emitted by the D5 catalog so the FE can coerce <Input> strings. */
export type PredicateParamType = 'str' | 'int' | 'bool' | 'float';

export interface PredicateLeafParam {
  name: string;
  type: PredicateParamType;
}

export interface PredicateLeaf {
  name: string;
  params: PredicateLeafParam[];
}

export async function listPredicateLeaves(): Promise<PredicateLeaf[]> {
  const res = await apiFetch(`${BASE_URL}/predicate-leaves/`);
  if (!res.ok) throw new Error('Failed to load predicate leaves');
  return res.json();
}

// ---------------------------------------------------------------------------
// MissionGiver (#729 — trigger-based givers: ROOM_TRIGGER / ENVIRONMENTAL_DETAIL)
// ---------------------------------------------------------------------------

export async function listGivers(
  filters: {
    giver_kind?: string;
    is_active?: boolean;
    name?: string;
    page?: number;
    page_size?: number;
  } = {}
): Promise<PaginatedResponse<MissionGiver>> {
  const res = await apiFetch(`${BASE_URL}/givers/${buildQueryString(filters)}`);
  if (!res.ok) throw new Error('Failed to load mission givers');
  return res.json();
}

export async function createGiver(body: Partial<MissionGiverRequest>): Promise<MissionGiver> {
  const res = await apiFetch(`${BASE_URL}/givers/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiValidationError(await res.json().catch(() => ({})));
  return res.json();
}

export async function patchGiver(
  id: number,
  body: Partial<MissionGiverRequest>
): Promise<MissionGiver> {
  const res = await apiFetch(`${BASE_URL}/givers/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiValidationError(await res.json().catch(() => ({})));
  return res.json();
}

export async function deleteGiver(id: number): Promise<void> {
  const res = await apiFetch(`${BASE_URL}/givers/${id}/`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete giver ${id}`);
}

const GIVER_TARGET_SEARCH_URL = '/api/mission-giver-targets/';

export async function searchMissionGiverTargets(
  kind: string,
  query: string
): Promise<EntitySearchResult[]> {
  const res = await apiFetch(
    `${GIVER_TARGET_SEARCH_URL}?kind=${encodeURIComponent(kind)}&search=${encodeURIComponent(query)}`
  );
  if (!res.ok) throw new Error('Failed to search mission giver targets');
  return res.json();
}

export async function resolveMissionGiverTarget(
  kind: string,
  id: number
): Promise<EntitySearchResult | null> {
  const res = await apiFetch(
    `${GIVER_TARGET_SEARCH_URL}?kind=${encodeURIComponent(kind)}&id=${id}`
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to resolve mission giver target');
  return res.json();
}

// ---------------------------------------------------------------------------
// #885 player journal/beat surface — the one PLAYER-scoped missions API
// (everything above is staff-only).
// ---------------------------------------------------------------------------

/**
 * Query string carrying the tab's browsing identity (#3479). The backend
 * reads `entry_id` as a query parameter on every MissionJournalViewSet /
 * MissionBoardViewSet operation (POSTs included), so this is appended to the
 * URL, never the body. Null/undefined means "omitted": the server falls back
 * to the account's durable selection. Never send an empty string (400).
 */
function entryIdQuery(entryId: number | null | undefined): string {
  return entryId != null ? `?entry_id=${entryId}` : '';
}

export async function listJournal(
  entryId?: number | null
): Promise<PaginatedResponse<JournalEntry>> {
  // Follow every page (2026-07 audit): the journal paginates at 25 and
  // concluded runs accumulate forever — older entries (and, depending on
  // ordering, active stories in the StoryTray) silently vanished past page 1.
  const results = await fetchAllPages<JournalEntry>(
    `${BASE_URL}/journal/${entryIdQuery(entryId)}`,
    'Failed to load your journal'
  );
  return { count: results.length, next: null, previous: null, results };
}

/**
 * Pending mission invites for the puppet — independent of participations (#audit2).
 * A brand-new character with zero missions has an empty journal, so invites
 * are read from a dedicated endpoint rather than journal entry 0.
 */
export async function listPendingInvites(entryId?: number | null): Promise<PendingMissionInvite[]> {
  const res = await apiFetch(`${BASE_URL}/journal/pending-invites/${entryIdQuery(entryId)}`);
  if (!res.ok) throw new Error('Failed to load your mission invites');
  return res.json() as Promise<PendingMissionInvite[]>;
}

export async function getBeat(
  instanceId: number,
  entryId?: number | null
): Promise<BeatView | null> {
  const res = await apiFetch(`${BASE_URL}/journal/${instanceId}/beat/${entryIdQuery(entryId)}`);
  if (res.status === 404) return null; // concluded run — journal shows the epilogue
  if (!res.ok) throw new Error('Failed to load the current beat');
  return res.json();
}

export async function resolveBeat(
  instanceId: number,
  body: {
    option_id: number;
    approach_id?: number | null;
    invoke_being_id?: number | null;
    invoke_line?: string;
  },
  entryId?: number | null
): Promise<ResolvedBeat> {
  const res = await apiFetch(`${BASE_URL}/journal/${instanceId}/resolve/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 400) {
    throw new ApiValidationError(await res.json());
  }
  if (!res.ok) throw new Error('Failed to resolve the option');
  return res.json();
}

// ---------------------------------------------------------------------------
// #2047 player tale surface.
// ---------------------------------------------------------------------------

export async function tellTale(
  instanceId: number,
  text: string,
  entryId?: number | null
): Promise<{ id: number; tale: string }> {
  const res = await apiFetch(`${BASE_URL}/journal/${instanceId}/tale/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (res.status === 400) {
    throw new ApiValidationError(await res.json());
  }
  if (!res.ok) throw new Error('Failed to save your tale');
  return res.json();
}

// ---------------------------------------------------------------------------
// #1753/#3040 mission after-action report surface.
// ---------------------------------------------------------------------------

/**
 * Report a RESOLVED run's outcome to its report-to Functionary, by style
 * (#1753). A 400 covers every gate the server enforces — the run isn't
 * RESOLVED, the style isn't offerable (e.g. Embellished without Persuasion),
 * or the reporter isn't co-located with a Functionary of the report-to role.
 * The caller surfaces the server's own message rather than pre-computing
 * reachability (#3040) — only the server knows who is standing where.
 */
export async function reportMission(
  instanceId: number,
  style: ReportStyle,
  entryId?: number | null
): Promise<MissionReportResult> {
  const res = await apiFetch(`${BASE_URL}/journal/${instanceId}/report/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style }),
  });
  if (res.status === 400) {
    throw new ApiValidationError(await res.json());
  }
  if (!res.ok) throw new Error('Failed to report this mission');
  return res.json();
}

// ---------------------------------------------------------------------------
// #1036 group beat + #887 invite surface (#2049 frontend).
// ---------------------------------------------------------------------------

export async function getGroupBeat(
  instanceId: number,
  entryId?: number | null
): Promise<GroupBeatResult> {
  const res = await apiFetch(
    `${BASE_URL}/journal/${instanceId}/group-beat/${entryIdQuery(entryId)}`
  );
  if (res.status === 404) return { group_beat: null, resolved: null };
  if (!res.ok) throw new Error('Failed to load the group beat');
  return res.json();
}

export async function submitGroupPick(
  instanceId: number,
  body: {
    option_id: number;
    approach_id?: number | null;
    invoke_being_id?: number | null;
    invoke_line?: string;
  },
  entryId?: number | null
): Promise<GroupBeatResult> {
  const res = await apiFetch(
    `${BASE_URL}/journal/${instanceId}/group-pick/${entryIdQuery(entryId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  if (res.status === 400) throw new ApiValidationError(await res.json());
  if (!res.ok) throw new Error('Failed to submit your pick');
  return res.json();
}

export async function castGroupVote(
  instanceId: number,
  body: { option_id: number },
  entryId?: number | null
): Promise<GroupBeatResult> {
  const res = await apiFetch(
    `${BASE_URL}/journal/${instanceId}/group-vote/${entryIdQuery(entryId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  if (res.status === 400) throw new ApiValidationError(await res.json());
  if (!res.ok) throw new Error('Failed to submit your vote');
  return res.json();
}

export async function inviteToMission(
  instanceId: number,
  body: { invitee_character_id: number },
  entryId?: number | null
): Promise<MissionInviteResult> {
  const res = await apiFetch(`${BASE_URL}/journal/${instanceId}/invite/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 400) throw new ApiValidationError(await res.json());
  if (!res.ok) throw new Error('Failed to send the invite');
  return res.json();
}

export async function respondToMissionInvite(
  body: {
    invite_id: number;
    response: 'accept' | 'decline';
  },
  entryId?: number | null
): Promise<MissionInviteResult> {
  const res = await apiFetch(`${BASE_URL}/journal/respond/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 400) throw new ApiValidationError(await res.json());
  if (!res.ok) throw new Error('Failed to respond to the invite');
  return res.json();
}

/** Search co-located characters for the invite picker (#2049). */
export async function searchRoomCharacters(query: string): Promise<EntitySearchResult[]> {
  const res = await apiFetch(`/api/characters/room/?search=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error('Failed to search room characters');
  return res.json();
}

// ---------------------------------------------------------------------------
// #2044 mission discovery — board postings + opportunities.
// ---------------------------------------------------------------------------

export interface OpportunityRow {
  name: string;
  summary: string;
  pointer: string;
  source_flavor: string;
}

export interface OpportunitiesResult {
  here: OpportunityRow[];
  nearby: OpportunityRow[];
  your_organizations: OpportunityRow[];
}

export async function getOpportunities(entryId?: number | null): Promise<OpportunitiesResult> {
  const res = await apiFetch(`${BASE_URL}/journal/opportunities/${entryIdQuery(entryId)}`);
  if (!res.ok) throw new Error('Failed to load opportunities');
  return res.json();
}

export interface BoardPosting {
  template_id: number;
  name: string;
  summary: string;
}

export async function getBoardPostings(
  boardObjectId: number,
  entryId?: number | null
): Promise<{ count: number; results: BoardPosting[] }> {
  const res = await apiFetch(
    `${BASE_URL}/boards/${boardObjectId}/postings/${entryIdQuery(entryId)}`
  );
  if (!res.ok) throw new Error('Failed to load board postings');
  return res.json();
}

export async function takeBoardPosting(
  boardObjectId: number,
  templateId: number,
  entryId?: number | null
): Promise<{ instance_id: number; template_id: number }> {
  const res = await apiFetch(`${BASE_URL}/boards/${boardObjectId}/take/${entryIdQuery(entryId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ template_id: templateId }),
  });
  if (res.status === 400) throw new ApiValidationError(await res.json());
  if (!res.ok) throw new Error('Failed to accept the posting');
  return res.json();
}
